import { useState } from "react";
import type { ReactNode } from "react";
import { TextArea } from "./Field";
import { ListPanel } from "./ListPanel";

/**
 * The comment box under a request: a textarea, an optional row of controls
 * (the staff screen puts its "Customer can see" toggle there; the portal has
 * nothing to put), and the Comment button. Owns only the draft and the
 * in-flight state — what a post means is the caller's `onPost`.
 */
export function CommentComposer({ onPost, controls }: { onPost: (body: string) => Promise<void>; controls?: ReactNode }) {
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);

  async function post() {
    const text = body.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      await onPost(text);
      setBody("");
    } finally {
      setPosting(false);
    }
  }

  return (
    <div className="border-t border-[var(--border)] p-4">
      <TextArea aria-label="Add a comment" value={body} onChange={(e) => setBody(e.target.value)} placeholder="Add a comment…" />
      <div className="mt-2 flex items-center justify-between">
        <div>{controls}</div>
        <button
          type="button"
          onClick={() => void post()}
          disabled={posting || body.trim() === ""}
          className="rounded-md bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700 focus-visible:focus-ring disabled:opacity-50"
        >
          Comment
        </button>
      </div>
    </div>
  );
}

/** The Comments section of a request screen: the thread, then the composer. */
export function CommentsPanel<T extends { id: string }>({
  id,
  comments,
  renderComment,
  onPost,
  controls,
}: {
  id: string;
  comments: T[];
  renderComment: (comment: T) => ReactNode;
  onPost: (body: string) => Promise<void>;
  controls?: ReactNode;
}) {
  return (
    <ListPanel id={id} title="Comments" items={comments} emptyText="No comments yet." renderItem={renderComment} footer={<CommentComposer onPost={onPost} controls={controls} />} />
  );
}
